import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Save, X, Lock, Boxes, Package } from "lucide-react";
import { logisticsApi, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

/**
 * Page Paramètres > Gestion Logistique
 * Phase 177 — CRUD catégories + articles + flag est_interne_administratif.
 */
export default function LogisticsSettingsPage() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState({ name: "", color: "#1d4ed8", est_interne_administratif: false });
  const [editingCat, setEditingCat] = useState({});
  const [newItem, setNewItem] = useState({ category_id: "", name: "" });
  const [editingItem, setEditingItem] = useState({});

  const reload = async () => {
    try {
      const [c, i] = await Promise.all([logisticsApi.getLogisticsCategories(), logisticsApi.getLogisticsCatalog()]);
      setCats(c.data || []);
      setItems(i.data || []);
    } catch (e) { toast.error(getErrorMessage(e, "Erreur de chargement")); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const createCat = async () => {
    if (!newCat.name.trim()) return;
    try {
      await logisticsApi.createLogisticsCategory({ ...newCat, order: cats.length + 1 });
      setNewCat({ name: "", color: "#1d4ed8", est_interne_administratif: false });
      reload();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };
  const saveCat = async (id) => {
    const d = editingCat[id]; if (!d) return;
    try {
      await logisticsApi.updateLogisticsCategory(id, d);
      setEditingCat((s) => ({ ...s, [id]: undefined }));
      reload();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };
  const delCat = async (id, name) => {
    if (!window.confirm(`Supprimer la catégorie "${name}" ? Les articles et tags rattachés seront aussi supprimés.`)) return;
    try { await logisticsApi.deleteLogisticsCategory(id); reload(); } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const createItem = async () => {
    if (!newItem.category_id || !newItem.name.trim()) return;
    try {
      await logisticsApi.createLogisticsCatalogItem(newItem);
      setNewItem({ category_id: "", name: "" });
      reload();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };
  const saveItem = async (id) => {
    const d = editingItem[id]; if (!d) return;
    try { await logisticsApi.updateLogisticsCatalogItem(id, d); setEditingItem((s) => ({ ...s, [id]: undefined })); reload(); }
    catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };
  const delItem = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try { await logisticsApi.deleteLogisticsCatalogItem(id); reload(); } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="logistics-settings-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <Boxes className="h-6 w-6 text-primary" /> Gestion Logistique
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configurez les catégories de tags (Matériaux, Appareils à louer, etc.) et le catalogue d'articles que vos commandes pourront utiliser.
        </p>
      </div>

      {/* Categories */}
      <Card data-testid="logistics-categories-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Catégories de tags</CardTitle>
          <p className="text-xs text-muted-foreground">Marquez les catégories internes (ex: "Blocages") pour qu'elles ne sortent jamais sur la fiche de chantier.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {cats.length === 0 && <p className="text-sm text-muted-foreground italic">Aucune catégorie. Commencez par "Matériaux à commander", "Appareil à louer", "Blocages internes"…</p>}
          {cats.map((c) => {
            const d = editingCat[c.id];
            if (d) {
              return (
                <div key={c.id} className="flex items-center gap-2 p-2 border rounded-md bg-zinc-50">
                  <input type="color" value={d.color} onChange={(e) => setEditingCat((s) => ({ ...s, [c.id]: { ...d, color: e.target.value } }))} className="w-8 h-8 rounded cursor-pointer border" />
                  <Input value={d.name} onChange={(e) => setEditingCat((s) => ({ ...s, [c.id]: { ...d, name: e.target.value } }))} className="h-8 flex-1" data-testid={`cat-edit-name-${c.id}`} />
                  <div className="flex items-center gap-1.5">
                    <Switch checked={!!d.est_interne_administratif} onCheckedChange={(v) => setEditingCat((s) => ({ ...s, [c.id]: { ...d, est_interne_administratif: v } }))} data-testid={`cat-edit-interne-${c.id}`} />
                    <Label className="text-xs">Interne</Label>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveCat(c.id)} data-testid={`cat-save-${c.id}`}><Save className="h-3.5 w-3.5 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingCat((s) => ({ ...s, [c.id]: undefined }))}><X className="h-3.5 w-3.5" /></Button>
                </div>
              );
            }
            return (
              <div key={c.id} className="flex items-center gap-2 p-2 border rounded-md" data-testid={`cat-row-${c.id}`}>
                <span className="w-4 h-4 rounded shrink-0" style={{ background: c.color }} />
                <Package className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-medium flex-1">{c.name}</span>
                {c.est_interne_administratif && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-zinc-200 text-zinc-700 px-1.5 py-0.5 rounded">
                    <Lock className="h-3 w-3" /> Interne
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground">{items.filter(i => i.category_id === c.id).length} article(s)</span>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingCat((s) => ({ ...s, [c.id]: { name: c.name, color: c.color || "#1d4ed8", est_interne_administratif: !!c.est_interne_administratif } }))} data-testid={`cat-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delCat(c.id, c.name)} data-testid={`cat-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-2 border-t">
            <input type="color" value={newCat.color} onChange={(e) => setNewCat({ ...newCat, color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border" />
            <Input placeholder="Ex: Matériaux à commander, Blocages internes…" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && createCat()} className="h-8 flex-1" data-testid="cat-new-name" />
            <div className="flex items-center gap-1.5">
              <Switch checked={newCat.est_interne_administratif} onCheckedChange={(v) => setNewCat({ ...newCat, est_interne_administratif: v })} data-testid="cat-new-interne" />
              <Label className="text-xs">Interne</Label>
            </div>
            <Button size="sm" onClick={createCat} disabled={!newCat.name.trim()} data-testid="cat-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Catalog items */}
      <Card data-testid="logistics-catalog-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Catalogue d'articles</CardTitle>
          <p className="text-xs text-muted-foreground">Articles prédéfinis (ex: "Nacelle 20m" → Appareil à louer). Sur la fiche commande, les utilisateurs choisiront dans ces listes.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {cats.length === 0 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Créez d'abord au moins une catégorie ci-dessus.</p>}
          {items.length === 0 && cats.length > 0 && <p className="text-sm text-muted-foreground italic">Aucun article. Ajoutez par exemple "Nacelle 20m" lié à "Appareil à louer".</p>}
          {items.map((it) => {
            const d = editingItem[it.id];
            const catName = cats.find(c => c.id === it.category_id)?.name || "?";
            if (d) {
              return (
                <div key={it.id} className="flex items-center gap-2 p-2 border rounded-md bg-zinc-50">
                  <Select value={d.category_id} onValueChange={(v) => setEditingItem((s) => ({ ...s, [it.id]: { ...d, category_id: v } }))}>
                    <SelectTrigger className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={d.name} onChange={(e) => setEditingItem((s) => ({ ...s, [it.id]: { ...d, name: e.target.value } }))} className="h-8 flex-1" data-testid={`item-edit-name-${it.id}`} />
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveItem(it.id)} data-testid={`item-save-${it.id}`}><Save className="h-3.5 w-3.5 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingItem((s) => ({ ...s, [it.id]: undefined }))}><X className="h-3.5 w-3.5" /></Button>
                </div>
              );
            }
            return (
              <div key={it.id} className="flex items-center gap-2 p-2 border rounded-md" data-testid={`item-row-${it.id}`}>
                <Package className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-medium flex-1">{it.name}</span>
                <span className="text-[11px] bg-zinc-100 px-1.5 py-0.5 rounded">{catName}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingItem((s) => ({ ...s, [it.id]: { name: it.name, category_id: it.category_id } }))} data-testid={`item-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delItem(it.id, it.name)} data-testid={`item-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-2 border-t">
            <Select value={newItem.category_id} onValueChange={(v) => setNewItem({ ...newItem, category_id: v })} disabled={cats.length === 0}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid="item-new-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
              <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Ex: Nacelle 20m, Ciment 35kg…" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && createItem()} className="h-8 flex-1" data-testid="item-new-name" />
            <Button size="sm" onClick={createItem} disabled={!newItem.category_id || !newItem.name.trim()} data-testid="item-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
